import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Divider,
  Popover,
  Switch,
  Typography,
  useTheme,
} from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Buildings,
  CaretRight,
  Check,
  Moon,
  SignOut,
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { useDarkMode } from '../../../hooks/useDarkMode';
import { CompanyService } from '../../../service/CompanyService';
import { UserService } from '../../../service/UserService';
import { AuthService } from '../../../service/AuthService';
import { useAuthStore } from '../../../store/storeAuth';
import { getUserInitials } from './layoutBetaUtils';

interface Company {
  id: string;
  name: string;
  logo?: string;
  role?: string;
  isCompanyOwner?: boolean;
  companyUsers?: { user: { id: string }; role: string }[];
}

interface UserMenuProps {
  anchorEl: HTMLButtonElement | null;
  isDarkMode: boolean;
  loggedInAsName: string;
  loggedInAsAvatarSrc?: string;
  onClose: () => void;
  onThemeToggle: () => void;
  onLogout: () => void;
}

export const UserMenu: React.FC<UserMenuProps> = ({
  anchorEl,
  loggedInAsName,
  loggedInAsAvatarSrc,
  onClose,
  onThemeToggle,
  onLogout,
}) => {
  const theme = useTheme();
  const { customColorPalette: palette } = theme;
  const isDarkMode = useDarkMode();
  const navigate = useNavigate();
  const { user: authUser, setAuthUser } = useAuthStore(state => state);

  const orgButtonRef = useRef<HTMLButtonElement | null>(null);
  const [orgPopoverOpen, setOrgPopoverOpen] = useState(false);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [myCompany, setMyCompany] = useState<Company | null>(null);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [switching, setSwitching] = useState(false);

  const loadCompanies = useCallback(async () => {
    if (companies.length > 0 || loadingCompanies) return;
    setLoadingCompanies(true);
    try {
      const res = await CompanyService.getUserCompanies();
      const all: Company[] = res?.data ?? [];
      const others = all.filter(c => !c.isCompanyOwner);
      const own = all.find(c => c.isCompanyOwner) ?? null;
      setCompanies(others);
      setMyCompany(own ?? null);
    } catch (error) {
      console.error('Failed to load companies:', error);
    } finally {
      setLoadingCompanies(false);
    }
  }, [companies.length, loadingCompanies]);

  useEffect(() => {
    if (!anchorEl) {
      setOrgPopoverOpen(false);
    }
  }, [anchorEl]);

  const handleOpenOrgPopover = (e: React.MouseEvent<HTMLButtonElement>) => {
    orgButtonRef.current = e.currentTarget;
    loadCompanies();
    setOrgPopoverOpen(true);
  };

  const handleCloseOrgPopover = () => {
    setOrgPopoverOpen(false);
  };

  const handleSwitchToPersonal = async () => {
    setSwitching(true);
    try {
      if (myCompany?.id) {
        await UserService.switchUserCompany({
          company: { id: myCompany.id, name: myCompany.name },
        });
      } else {
        await UserService.switchUserCompany({ personal: true });
      }
      const res = await AuthService.getMe();
      setAuthUser({ ...res.data });
      handleCloseOrgPopover();
      onClose();
      navigate('/dashboard-beta');
    } catch (error) {
      console.error('Failed to switch to personal:', error);
    } finally {
      setSwitching(false);
    }
  };

  const handleSwitchToCompany = async (company: Company) => {
    setSwitching(true);
    try {
      await UserService.switchUserCompany({
        company: { id: company.id, name: company.name },
      });
      const res = await AuthService.getMe();
      setAuthUser({ ...res.data });
      handleCloseOrgPopover();
      onClose();
      navigate('/dashboard-beta');
    } catch (error) {
      console.error('Failed to switch to company:', error);
    } finally {
      setSwitching(false);
    }
  };

  const isCurrentOrg = (companyId: string) =>
    authUser?.selectedCompanyId === companyId;
  const isPersonalActive =
    !authUser?.selectedCompanyId ||
    (myCompany?.id && authUser?.selectedCompanyId === myCompany?.id);

  const getRoleLabel = (company: Company) => {
    if (company.isCompanyOwner) return 'Admin';
    if (!company.role) return '';
    if (company.role === 'employee') return 'Org User';
    return company.role.charAt(0).toUpperCase() + company.role.slice(1);
  };

  const hasLoggedInPhoto = Boolean(loggedInAsAvatarSrc?.trim());

  return (
    <>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              backgroundColor: isDarkMode
                ? palette.gray[900]
                : palette.gray.A100,
              borderRadius: '8px',
              marginTop: '8px',
              minWidth: '220px',
              maxWidth: '280px',
              boxShadow:
                '0px 4px 6px -2px rgba(0, 0, 0, 0.05), 0px 10px 15px -3px rgba(0, 0, 0, 0.1)',
            },
          },
        }}
      >
        <Box sx={{ padding: '8px' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '4px 6px 12px',
            }}
          >
            <Avatar
              src={hasLoggedInPhoto ? loggedInAsAvatarSrc : undefined}
              alt={loggedInAsName}
              sx={{
                width: '40px',
                height: '40px',
                borderRadius: '8px',
                backgroundColor: palette.blue[500],
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: palette.gray.A100,
                fontSize: '14px',
                fontWeight: 600,
              }}
            >
              {!hasLoggedInPhoto
                ? getUserInitials(loggedInAsName || null)
                : null}
            </Avatar>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                minWidth: 0,
                gap: '2px',
              }}
            >
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 400,
                  lineHeight: 1.3,
                  color: palette.gray[500],
                }}
              >
                Logged in as
              </Typography>
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 700,
                  lineHeight: 1.3,
                  color: isDarkMode ? palette.gray[100] : palette.neutral[900],
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {loggedInAsName}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: '8px' }} />

          <Button
            onClick={handleOpenOrgPopover}
            disabled={switching}
            sx={{
              width: '100%',
              justifyContent: 'space-between',
              textTransform: 'none',
              color: isDarkMode ? palette.gray[300] : palette.gray[600],
              padding: '8px 12px',
              borderRadius: '6px',
              '&:hover': {
                backgroundColor: isDarkMode
                  ? palette.gray[800]
                  : palette.gray[200],
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Buildings size={20} color={palette.gray[500]} />
              <Typography sx={{ fontSize: '14px' }}>Switch Org</Typography>
            </Box>
            <CaretRight size={16} color={palette.gray[500]} />
          </Button>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              userSelect: 'none',
              padding: '8px 12px',
              borderRadius: '6px',
              gap: '4px',
              '&:hover': {
                backgroundColor: isDarkMode
                  ? palette.gray[800]
                  : palette.gray[200],
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Moon size={20} color={palette.gray[500]} />
              <Typography
                sx={{
                  fontSize: '14px',
                  color: isDarkMode ? palette.gray[300] : palette.gray[600],
                }}
              >
                Dark Mode
              </Typography>
            </Box>
            <Switch
              checked={isDarkMode}
              onChange={onThemeToggle}
              size='small'
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: palette.blue[500],
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: palette.blue[500],
                },
              }}
            />
          </Box>

          <Divider sx={{ margin: '8px 0px' }} />

          <Button
            onClick={onLogout}
            sx={{
              width: '100%',
              justifyContent: 'flex-start',
              textTransform: 'none',
              color: isDarkMode ? palette.gray[300] : palette.gray[600],
              padding: '8px 12px',
              borderRadius: '6px',
              '&:hover': {
                backgroundColor: isDarkMode
                  ? palette.gray[800]
                  : palette.gray[200],
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <SignOut size={20} color={palette.gray[500]} />
              <Typography sx={{ fontSize: '14px' }}>Logout</Typography>
            </Box>
          </Button>
        </Box>
      </Popover>

      <Popover
        open={orgPopoverOpen}
        anchorEl={orgButtonRef.current}
        onClose={handleCloseOrgPopover}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              backgroundColor: isDarkMode
                ? palette.gray[900]
                : palette.gray.A100,
              borderRadius: '8px',
              marginLeft: '4px',
              minWidth: '260px',
              maxWidth: '320px',
              maxHeight: '360px',
              overflowY: 'auto',
              boxShadow:
                '0px 4px 6px -2px rgba(0, 0, 0, 0.05), 0px 10px 15px -3px rgba(0, 0, 0, 0.1)',
            },
          },
        }}
      >
        <Box sx={{ padding: '8px' }}>
          {loadingCompanies ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={20} />
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                }}
              >
                {authUser?.registrationMethod !== 'invite' && (
                  <Box
                    aria-current={isPersonalActive ? 'true' : undefined}
                    onClick={
                      isPersonalActive ? undefined : handleSwitchToPersonal
                    }
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '6px 8px',
                      borderRadius: '6px',
                      cursor: isPersonalActive ? 'default' : 'pointer',
                      backgroundColor: isPersonalActive
                        ? isDarkMode
                          ? palette.gray[800]
                          : palette.gray[200]
                        : 'transparent',
                      opacity: switching ? 0.5 : 1,
                      pointerEvents: switching ? 'none' : 'auto',
                      '&:hover': {
                        backgroundColor: isDarkMode
                          ? palette.gray[800]
                          : palette.gray[200],
                      },
                    }}
                  >
                    <Avatar
                      src={myCompany?.id ? myCompany.logo : authUser?.avatar}
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '6px',
                        backgroundColor: palette.blue[500],
                        fontSize: '12px',
                        flexShrink: 0,
                      }}
                    >
                      {myCompany?.id ? (
                        <Buildings size={18} />
                      ) : (
                        authUser?.name?.[0] ?? ''
                      )}
                    </Avatar>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        sx={{
                          fontSize: '14px',
                          fontWeight: 500,
                          color: isDarkMode
                            ? palette.gray[200]
                            : palette.gray[700],
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {myCompany?.name ?? authUser?.name ?? 'Personal'}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '12px',
                          color: palette.gray[500],
                          textTransform: 'capitalize',
                        }}
                      >
                        {myCompany?.role ? getRoleLabel(myCompany) : 'Owner'}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        width: 24,
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {isPersonalActive ? (
                        <Check
                          size={18}
                          weight='bold'
                          color={palette.blue[500]}
                        />
                      ) : null}
                    </Box>
                  </Box>
                )}

                {companies.map(company => (
                  <Box
                    key={company.id}
                    aria-current={isCurrentOrg(company.id) ? 'true' : undefined}
                    onClick={
                      isCurrentOrg(company.id)
                        ? undefined
                        : () => handleSwitchToCompany(company)
                    }
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '6px 8px',
                      borderRadius: '6px',
                      cursor: isCurrentOrg(company.id) ? 'default' : 'pointer',
                      backgroundColor: isCurrentOrg(company.id)
                        ? isDarkMode
                          ? palette.gray[800]
                          : palette.gray[200]
                        : 'transparent',
                      opacity: switching ? 0.5 : 1,
                      pointerEvents: switching ? 'none' : 'auto',
                      '&:hover': {
                        backgroundColor: isDarkMode
                          ? palette.gray[800]
                          : palette.gray[200],
                      },
                    }}
                  >
                    <Avatar
                      src={company.logo}
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '6px',
                        backgroundColor: palette.blue[500],
                        fontSize: '12px',
                        flexShrink: 0,
                      }}
                    >
                      <Buildings size={18} />
                    </Avatar>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        sx={{
                          fontSize: '14px',
                          fontWeight: 500,
                          color: isDarkMode
                            ? palette.gray[200]
                            : palette.gray[700],
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {company.name}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '12px',
                          color: palette.gray[500],
                          textTransform: 'capitalize',
                        }}
                      >
                        {getRoleLabel(company)}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        width: 24,
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {isCurrentOrg(company.id) ? (
                        <Check
                          size={18}
                          weight='bold'
                          color={palette.blue[500]}
                        />
                      ) : null}
                    </Box>
                  </Box>
                ))}
              </Box>

              {!loadingCompanies &&
                companies.length === 0 &&
                authUser?.registrationMethod === 'invite' && (
                  <Typography
                    sx={{
                      fontSize: '13px',
                      color: palette.gray[500],
                      textAlign: 'center',
                      py: 1,
                    }}
                  >
                    No organizations available
                  </Typography>
                )}
            </>
          )}
        </Box>
      </Popover>
    </>
  );
};
